import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { BidDriverSelection } from '@numeo/types'
import { CanonicalLoad, ClientApplication, ClientUser, BodyEmailTemplate } from '@numeo/types-core'
import { useAuth } from 'hooks/auth'
import axios from 'axios'
import { cn } from 'utils/cn'
import { escapeHtml } from 'utils/escape-html'
import { showSnackbar, SnackbarType } from 'utils/snackbar'
import { TiptapEditor } from '@/components/tiptap/TiptapEditor'
import type { HtmlPreviewRoleResolver } from '@/components/tiptap/extensions/PreservedHtml'
import { SignatureFooterEditor } from '@/components/tiptap/SignatureFooterEditor'
import { buildSignatureTable, extractSignatureLogoSrc } from '@/components/tiptap/utils'
import { saveEmailTemplate } from '@/pages/dashboard/pages/settings/api/EmailSettings'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { convertCanonicalLoadToV4Payload } from '../../utils/emailPayload'
import { getCurrentEmailConfig } from '../../hooks/usePrivateLoadEmail'
import {
    ComposeEmailType,
    DRIVER_TEMPLATE_VARIABLE_KEYS,
    resolveBodyTemplates,
    resolveSavedTemplate,
    resolveSenderMailbox,
    TYPE_TO_TEMPLATE_TYPE,
} from './savedTemplate'
import { useEmailReconnectOnAuthError } from '../../hooks/useEmailReconnectOnAuthError'
import {
    applyTemplateVariables,
    ensureHtmlLineBreaks,
    getPositiveEmailRate,
    prepareEmailContentForSend,
    RATE_SLOT_MARKER,
    synchronizeEmailTemplateRate,
} from '../../utils/emailTemplateUtils'
import { EmailAnalyticsSurface, isLoadHubEmailSurface } from '../../utils/emailAnalytics'
import { mergeCcRecipients, splitRecipientEmails } from '../../utils/recipientEmails'
import { resolveSenderCompanyData } from '../../utils/resolveSenderCompanyData'
import { beginEmailSend } from '../../utils/emailSendLifecycle'
import { sendOfferBookEmailCanonical } from '../../api/emailApi'
import { trackEmailAttempted, trackEmailCompleted } from '../../utils/sourceAnalytics'
import {
    toDetailedEmailType,
    toServerEmailAnalytics,
    trackEmailRequestFailed,
    trackEmailValidationFailed,
    trackEmailSendSubmitted,
} from '../../utils/loadAttributionAnalytics'

/** Shows the body read-only instead of in the editor, for templates the editor's schema cannot hold. */
export interface ReadOnlyBodyRenderer {
    /** Judged on the RAW template — before variables, so markup the compose injects ({{company_logo}}'s <img>)
     *  never counts. Asked once per template, not per keystroke. */
    appliesTo: (templateBody: string) => boolean
    render: (bodyHtml: string) => React.ReactNode
}

export interface ComposeEmailTemplateDefinition {
    subject: string
    body: string
    signature: string
    previewRoleResolver?: HtmlPreviewRoleResolver
}

interface OfferBookEmailComposeProps {
    load: CanonicalLoad
    type: 'offer' | 'book' | 'info'
    /** Explicit template pick (Box Truck's Info template dropdown): this template wins resolution
     *  outright — over the type-matched template and the config's selected one. Omitted → unchanged. */
    templateOverrideId?: string
    onClose: () => void
    /** Fires after validation passes and the send request starts, without waiting for its response. */
    onSendStarted?: () => void
    /** Fires after a successful send. Carries the new thread id + persisted load id from the
     *  response so callers can switch straight to the thread without re-querying (avoids the
     *  fetchEmailThreadIds propagation race). */
    onSent?: (info?: { emailThreadId?: string; loadId?: string; alreadySent?: boolean; sentRate?: number | null; bodyEdited?: boolean }) => void
    /**
     * Bid mode (Box Truck Bidding): overrides the load's rate for {{rate}}/{{rpm}} interpolation and
     * for the persisted/sent load, and live-re-seeds the template as it changes — but only while the
     * user hasn't hand-edited (edits win). Omitted → classic load-hub behaviour (seed once from load).
     */
    rateOverride?: number | null
    /**
     * The driver this bid is for, recorded with it. Pass ONLY a driver the user picked.
     *
     * Load Hub passes none — it has no driver to switch between. Box Truck's drawer shows the nearest
     * driver's unit details as a fallback while nothing is picked, and that one must not arrive here:
     * it would put a name against a bid the operator never aimed at that truck.
     */
    bidDriver?: BidDriverSelection | null
    /** Send-button label (e.g. "Place Bid" for bidding). Defaults to "Send". */
    sendLabel?: string
    /** Render the send button as a large primary (blue) CTA — used by Box Truck Bidding. */
    sendPrimary?: boolean
    /** Override the recipient (testing hook — e.g. localStorage `testEmail` for Box Truck Bidding). */
    toOverride?: string
    /** When set, the Cancel/Send action bar is portaled into this element instead of rendering inline —
     *  Box Truck Bidding pins it under the calculator so it stays visible in the side-by-side layout. */
    actionsContainer?: HTMLElement | null
    /** Optional control rendered directly under the To/From row — Box Truck's draft-email drawer drops
     *  its "Send as" sender picker here so it sits next to the From address instead of above the tabs. */
    senderSelector?: React.ReactNode
    /** Optional control pinned to the right end of the To/From row — Box Truck puts its "Edit template"
     *  pencil here, beside the email it edits, instead of in the crowded tabs row above the calculator. */
    headerAction?: React.ReactNode
    /** Hide the Cancel button (Box Truck's drawer closes via its own chrome, so the extra button is noise). */
    hideCancel?: boolean
    /** The selected sender email (Box Truck "Send as"). Passing it drives a re-render + guarded re-seed on
     *  sender change INSTEAD of remounting — so switching mailbox refreshes the company signature (and the
     *  subject/body if untouched) without nuking the operator's in-progress draft. */
    senderKey?: string
    /** Extra {{variable}} values interpolated BEFORE the load/company pass (which would otherwise consume
     *  some of these tokens itself) — Box Truck feeds the selected (or nearest) driver's fields here. For
     *  callers that pass this, the known driver keys are ALWAYS replaced ('-' when absent) so a driver
     *  variable saved into a shared template never sends raw {{braces}}; callers that omit it (Load Hub)
     *  keep literal {{driver_*}} text untouched. */
    extraVariables?: Record<string, string | null | undefined>
    /** Hide the "Info/Offer/Booking Email" title row incl. its ✕ (Box Truck's bottom pane — the tabs already
     *  say which email this is, and the drawer has its own close). */
    hideHeader?: boolean
    /** Hide the CC input (Box Truck — operators never CC broker bids; the template's CC list still applies). */
    hideCc?: boolean
    /** Stretch the send button across its container (Box Truck pins it full-width under the calculator). */
    fullWidthSend?: boolean
    /** Bundled templates to fall back on when the sender has none saved for this type. Defaults to the
     *  shared DEFAULT_TEMPLATES; Box Truck passes its own so its bid card can differ without changing what
     *  Load Hub sends. */
    defaultTemplates?: Record<ComposeEmailType, ComposeEmailTemplateDefinition>
    /** Seed from `defaultTemplates` even when the sender has a saved template for this type. Box Truck's
     *  default (no-tab-picked) state uses it so the board's own bid template is what opens; picking a tab
     *  clears it and the saved template resolves as usual. */
    forceBundledTemplate?: boolean
    /** Let the body editor keep hand-authored HTML (Box Truck's bid card). Off elsewhere so Load Hub's
     *  plain-text templates round-trip exactly as they did. */
    preserveHtml?: boolean
    /** Show the body read-only, rendered by the caller, instead of in the editor — for HTML the editor's schema
     *  cannot hold (a Box Truck table with column definitions): the editor would show it reshaped, and send
     *  it reshaped once touched. A read-only body is never marked edited, so the send re-interpolates the
     *  template at the current rate. Pass a stable object (module-level): it is a memo dependency. */
    readOnlyBody?: ReadOnlyBodyRenderer
    /** Shrink a body wider than the pane to fit it (Box Truck's designed table templates), so it stays editable
     *  without running off the side. Screen only — the sent HTML is not scaled. */
    fitBodyToWidth?: boolean
    /** Hide the signature editor AND leave the signature off the sent mail. For a template that already
     *  carries its own footer (Box Truck's bid card) — appending the company block again would print it
     *  twice. The company LOGO is still resolved and handed to `renderBody` so the template can place it. */
    hideSignature?: boolean
    /** Identifies reuse outside Load Hub so those sends do not pollute Load Hub usage analytics. */
    analyticsSurface?: EmailAnalyticsSurface
    analyticsComposeId?: string
}

// Default offer/book/info templates (raw, with {{variables}}). Body holds the message; signature holds
// the company block. Seeded to the user's templates on first send when they have none for the type.
// Exported so the inline template editor seeds from the same source of truth.
export const DEFAULT_TEMPLATES: Record<ComposeEmailType, ComposeEmailTemplateDefinition> = {
    info: {
        subject: 'Is this load available? {{origin}} to {{destination}} on {{pickup_date}}',
        body: [
            'Hi,',
            '',
            'Is your load from {{origin}} to {{destination}} on {{pickup_date}} still available?',
            '',
            '- Equipment: {{equipment}}',
            '- Trip Miles: {{trip_miles}} mi',
            '- Weight: {{weight}} lbs',
            '',
            'Could you share the rate and any requirements? Thanks.',
            '',
            'Best regards',
        ].join('\n'),
        signature: ['{{company}}', 'MC: {{mc}}{{dot_line}}', '{{phone_line}}'].join('\n'),
    },
    offer: {
        subject: 'Quote ${{rate}} for {{origin}} to {{destination}} on {{pickup_date}} ({{trip_miles}} mi)',
        body: [
            'Hi,',
            '',
            'We can move {{origin}} to {{destination}} on {{pickup_date}} with the following:',
            '',
            '- Equipment: {{equipment}}',
            '- Rate: ${{rate}}',
            '- RPM: ${{rpm}}/mi',
            '- Trip Miles: {{trip_miles}} mi',
            '- Weight: {{weight}} lbs',
            '',
            'Best regards',
        ].join('\n'),
        signature: ['{{company}}', 'MC: {{mc}}{{dot_line}}', '{{phone_line}}'].join('\n'),
    },
    book: {
        subject: "Let's book it! {{origin}} to {{destination}} on {{pickup_date}}",
        body: [
            'Hi,',
            '',
            'We would like to book this load. Please send the rate confirmation.',
            '',
            '- {{origin}} to {{destination}}',
            '- Pickup: {{pickup_date}}',
            '- Equipment: {{equipment}}',
            '- Rate: ${{rate}}',
            '',
            'Best regards',
        ].join('\n'),
        signature: ['{{company}}', 'MC: {{mc}}{{dot_line}}', '{{phone_line}}'].join('\n'),
    },
}

export const OfferBookEmailCompose: React.FC<OfferBookEmailComposeProps> = ({
    load,
    type,
    templateOverrideId,
    onClose,
    onSendStarted,
    onSent,
    rateOverride,
    bidDriver,
    sendLabel = 'Send',
    sendPrimary = false,
    toOverride,
    actionsContainer,
    senderSelector,
    headerAction,
    hideCancel = false,
    senderKey,
    extraVariables,
    hideHeader = false,
    hideCc = false,
    fullWidthSend = false,
    defaultTemplates = DEFAULT_TEMPLATES,
    forceBundledTemplate = false,
    preserveHtml = false,
    readOnlyBody,
    fitBodyToWidth = false,
    hideSignature = false,
    analyticsSurface,
    analyticsComposeId,
}) => {
    const { user, application, organizationData, setUser, setApplication, verifyAuth } = useAuth()
    const handleEmailAuthError = useEmailReconnectOnAuthError(verifyAuth)

    const config = getCurrentEmailConfig()
    const nylasEmail = useMemo(() => resolveSenderMailbox(user?.nylasEmails, config?.selectedEmail), [user, config])

    const senderEmail = nylasEmail?.email || ''
    const contactEmails = useMemo(
        () => splitRecipientEmails(toOverride?.trim() || load.brokerContactEmail?.trim() || load.posterContactEmail?.trim() || ''),
        [toOverride, load.brokerContactEmail, load.posterContactEmail]
    )
    const contactEmail = contactEmails[0] || ''

    const companySignature = useMemo(() => {
        const userTemplates = resolveBodyTemplates(nylasEmail, user)
        return (
            userTemplates.find((t) => t.signature && extractSignatureLogoSrc(t.signature))?.signature ||
            userTemplates.find((t) => t.signature)?.signature ||
            defaultTemplates[type].signature
        )
        // `user` is excluded deliberately: resolveBodyTemplates reads only user.emailTemplates.bodyTemplates,
        // which IS listed. Everything else the body reads — including defaultTemplates — is in the array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nylasEmail, user?.emailTemplates?.bodyTemplates, type, defaultTemplates])

    const companyData = useMemo(
        () =>
            resolveSenderCompanyData({
                selectedCompanyDetail: nylasEmail?.companyDetail,
                nylasEmails: user?.nylasEmails,
                application,
                companyFromParentEnabled: organizationData?.companyFromParentEnabled,
                userName: user?.name || '',
                userEmail: nylasEmail?.email || '',
                // Lifted out of the resolved signature so a template carrying its own footer can place the
                // logo itself ({{company_logo}}) instead of relying on the signature block being appended.
                companyLogoUrl: extractSignatureLogoSrc(companySignature) || '',
            }),
        [nylasEmail, user?.name, user?.nylasEmails, application, organizationData?.companyFromParentEnabled, companySignature]
    )

    // Resolve the user's saved template for this action (BID for offer, BROKER for book), then the
    // currently selected template; null falls back to the bundled default. `hadTemplate` drives seed-once.
    const resolvedTemplate = useMemo<BodyEmailTemplate | null>(
        () =>
            // Null resolves to the bundled default downstream — which is exactly what the caller is asking
            // for. Every other rule lives in the resolver, which Box Truck's drawer also calls to decide
            // whether to ask for the bundled card in the first place.
            forceBundledTemplate
                ? null
                : resolveSavedTemplate(user, {
                      type,
                      selectedEmail: config?.selectedEmail,
                      selectedTemplateId: config?.selectedTemplateId,
                      overrideId: templateOverrideId,
                  }),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the resolver reads only these fields of `user`, covered below
        [
            user?.nylasEmails,
            user?.emailTemplates?.bodyTemplates,
            config?.selectedEmail,
            config?.selectedTemplateId,
            type,
            templateOverrideId,
            forceBundledTemplate,
        ]
    )

    const hadTemplateRef = useRef<boolean>(!!resolvedTemplate)

    // The signature is ONE company/footer block with the company LOGO inline — not per-email-type. Keying the
    // seed off the current type's template drops the logo on Offer/Book, because a type's template can be MISSING
    // body+subject (so `resolvedTemplate` is null and the logo-less DEFAULT gets used — the actual bug on this
    // user's BID/BROKER templates) or be logo-less. Resolve the ONE company signature independent of type: prefer
    // a saved signature that actually carries the logo, then any saved signature, then the bundled default.
    // The load whose rate feeds {{rate}}/{{rpm}} — the bid overrides it when set (Box Truck Bidding).
    const rateLoad = useCallback((rate?: number | null): CanonicalLoad => (rate != null ? { ...load, rateAmountUsd: rate } : load), [load])

    // Serialized for effect deps; the memo below rebuilds the object only when its CONTENT changes, so a
    // fresh object literal from the caller every render can't re-trigger seeds.
    const extraVariablesKey = JSON.stringify(extraVariables ?? null)
    const extraVars = useMemo<Record<string, string | null | undefined> | null>(
        () => (extraVariablesKey === 'null' ? null : (JSON.parse(extraVariablesKey) as Record<string, string | null | undefined>)),
        [extraVariablesKey]
    )

    // Build interpolated subject/body/signature for a given rate. The signature logo (if any) is
    // already inline in the template signature, so it renders/edits in the SignatureFooterEditor.
    const buildSeed = useCallback(
        (rate?: number | null) => {
            const tpl = resolvedTemplate ?? defaultTemplates[type]
            const l = rateLoad(rate)
            // Caller-provided variables (Box Truck driver fields) interpolate BEFORE the load/company pass —
            // that pass already consumes some of these tokens itself ({{driver_name}}/{{driver_phone}} → ''),
            // so running after it could never fill them. Rules:
            //  - values entering the HTML BODY are escaped (driver-controlled free text must never become
            //    markup — the rule the retired bodyPrefix path enforced); the subject is plain text → raw;
            //  - a replacement FUNCTION sidesteps String.replace's `$&`/`$'` pattern semantics in values;
            //  - the known driver keys ALWAYS resolve ('-' when absent) so a template holding them never
            //    sends raw {{braces}} — but only for callers that opted in by passing extraVariables. Load
            //    Hub passes none, so its literal {{driver_*}} text stays visibly intact, as before.
            const applyExtras = (s: string, escapeValues: boolean) => {
                if (!extraVars) return s
                const extras = { ...extraVars }
                for (const key of DRIVER_TEMPLATE_VARIABLE_KEYS) extras[key] = extras[key] || '-'
                return Object.entries(extras).reduce((acc, [key, value]) => {
                    const replacement = escapeValues ? escapeHtml(value || '-') : value || '-'
                    return acc.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), () => replacement)
                }, s)
            }
            return {
                subject: applyTemplateVariables(applyExtras(tpl.subject, false), l, companyData),
                body: ensureHtmlLineBreaks(applyTemplateVariables(applyExtras(tpl.body, true), l, companyData)),
                // Always the ONE company signature (with its logo) resolved above — never the per-type template's
                // own `.signature`, which may be absent (→ logo-less default) or logo-less on this type.
                signature: ensureHtmlLineBreaks(applyTemplateVariables(companySignature, l, companyData)),
                cc: (resolvedTemplate?.cc || []).join(', '),
            }
        },
        [resolvedTemplate, type, rateLoad, companyData, companySignature, extraVars, defaultTemplates]
    )

    // Seed once at mount (with the initial bid, if any).
    const initialContent = useMemo(() => buildSeed(rateOverride), []) // eslint-disable-line react-hooks/exhaustive-deps

    const [subject, setSubject] = useState(initialContent.subject)
    const [bodyHtml, setBodyHtml] = useState(initialContent.body)
    const [signatureHtml, setSignatureHtml] = useState(initialContent.signature)
    const [cc, setCc] = useState(initialContent.cc)
    const [sending, setSending] = useState(false)

    // Once the user hand-edits, the calculator stops overwriting their text. Edits are only counted
    // once the editor has been focused — Tiptap fires a spurious onChange while normalizing the seeded
    // HTML at mount, which must NOT be mistaken for a hand edit (it would freeze the live rate).
    const bodyEditedRef = useRef(false)
    const bodyFocusedRef = useRef(false)
    const subjectEditedRef = useRef(false)
    const mountedRef = useRef(false)

    // Bid mode only: live-re-seed the template as the bid changes. Once the dispatcher edits the
    // subject/body, update only the template's rate slot so their MC/dimensions/copy stay untouched while
    // the visible draft continues to agree with Price for Broker.
    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true
            return
        }
        // Only classic load-hub (no bid) passes no rateOverride at all → never live-re-seed. In bid mode
        // rateOverride is number|null: `null` = the price field was CLEARED, which must still re-seed
        // (buildSeed(null) uses the load's rate — the same value the send falls back to), not freeze the
        // body at the last typed price and diverge from what actually gets sent.
        if (rateOverride === undefined) return
        const seed = buildSeed(rateOverride)
        // Where the template puts {{rate}}, so an edited draft's rate slots — and only those — follow the bid.
        const rateLayout = buildSeed(RATE_SLOT_MARKER)
        const effectiveRate = rateLoad(rateOverride).rateAmountUsd
        setBodyHtml((draft) => (bodyEditedRef.current && type === 'offer' ? synchronizeEmailTemplateRate(draft, rateLayout.body, effectiveRate) : seed.body))
        setSubject((draft) =>
            subjectEditedRef.current && type === 'offer' ? synchronizeEmailTemplateRate(draft, rateLayout.subject, effectiveRate) : seed.subject
        )
    }, [rateOverride, buildSeed, rateLoad, type])

    // Sender switched (Box Truck "Send as"): re-seed the company signature to the new mailbox and re-seed
    // subject/body ONLY if the operator hasn't hand-edited them — never nuke an in-progress draft (the
    // compose is no longer remounted on sender change). Skips the initial mount (prev === current).
    const prevSenderKeyRef = useRef(senderKey)
    useEffect(() => {
        if (prevSenderKeyRef.current === senderKey) return
        prevSenderKeyRef.current = senderKey
        const seed = buildSeed(rateOverride)
        setSignatureHtml(seed.signature)
        if (!bodyEditedRef.current) setBodyHtml(seed.body)
        if (!subjectEditedRef.current) setSubject(seed.subject)
    }, [senderKey, buildSeed, rateOverride])

    // Extra variables changed WITHOUT a remount (e.g. the nearest driver resolved after the fetch, or the
    // pick changed): re-seed subject/body unless hand-edited — mirrors the sender-switch effect above.
    const prevExtraVarsRef = useRef(extraVariablesKey)
    useEffect(() => {
        if (prevExtraVarsRef.current === extraVariablesKey) return
        prevExtraVarsRef.current = extraVariablesKey
        const seed = buildSeed(rateOverride)
        if (!bodyEditedRef.current) setBodyHtml(seed.body)
        if (!subjectEditedRef.current) setSubject(seed.subject)
    }, [extraVariablesKey, buildSeed, rateOverride])

    const handleBodyChange = useCallback((html: string) => {
        if (bodyFocusedRef.current) bodyEditedRef.current = true
        setBodyHtml(html)
    }, [])

    // The same template buildSeed interpolates, so the caller judges exactly what the body was seeded from.
    const templateBody = (resolvedTemplate ?? defaultTemplates[type]).body
    const bodyIsReadOnly = useMemo(() => readOnlyBody?.appliesTo(templateBody) ?? false, [readOnlyBody, templateBody])

    const handleLogoUpload = useCallback(
        async (file: File): Promise<string> => {
            if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
                showSnackbar(SnackbarType.ERROR, 'Only JPEG and PNG images are allowed')
                return ''
            }
            if (file.size > 5 * 1024 * 1024) {
                showSnackbar(SnackbarType.ERROR, 'Image must be under 5MB')
                return ''
            }
            try {
                const emailParam = senderEmail ? `?email=${encodeURIComponent(senderEmail)}` : ''
                const formData = new FormData()
                formData.append('file', file)
                const response = await axios.post(`/email/signature-logo${emailParam}`, formData)
                return response.data?.signatureLogoUrl || ''
            } catch {
                showSnackbar(SnackbarType.ERROR, 'Failed to upload logo')
                return ''
            }
        },
        [senderEmail]
    )

    // Persist on send so an uploaded signature logo sticks. Saves the RAW signature template
    // (variables intact) with the editor's logo merged in — body/subject/cc are NOT overwritten
    // (they reuse the resolved template or the bundled default). Runs when the logo changed, or to
    // seed the default for a user who has no template for this type yet. Logo removal and signature
    // text edits are managed in Settings, not here.
    const persistTemplate = useCallback(async () => {
        // An explicit template pick composes from a DIFFERENT template than this type's own — persisting
        // here would copy the picked template's subject/body over the type template. Skip entirely.
        if (templateOverrideId) return
        const base = resolvedTemplate
        const baseSig = base?.signature ?? defaultTemplates[type].signature
        const editedLogo = extractSignatureLogoSrc(signatureHtml)
        const baseLogo = extractSignatureLogoSrc(baseSig)
        const logoChanged = (editedLogo || '') !== (baseLogo || '')
        if (!logoChanged && hadTemplateRef.current) return

        let signatureToSave = baseSig
        if (editedLogo) {
            signatureToSave = baseLogo
                ? baseSig.replace(/(<img\b[^>]*\bdata-signature-logo\b[^>]*\bsrc\s*=\s*")[^"]*(")/i, `$1${editedLogo}$2`)
                : buildSignatureTable(editedLogo, ensureHtmlLineBreaks(baseSig))
        }

        try {
            const res = await saveEmailTemplate({
                type: TYPE_TO_TEMPLATE_TYPE[type],
                subject: base?.subject ?? defaultTemplates[type].subject,
                body: base?.body ?? defaultTemplates[type].body,
                signature: signatureToSave,
                emailFooter: signatureToSave,
                cc: base?.cc ?? [],
            })
            if (res?.data?.user) setUser(res.data.user as ClientUser)
            if (res?.data?.application) setApplication(res.data.application as ClientApplication)
            hadTemplateRef.current = true
        } catch (error) {
            console.error('Email sent, but template persistence failed', error)
            showSnackbar(SnackbarType.WARNING, 'Email sent, but your signature or template changes could not be saved.')
        }
    }, [resolvedTemplate, signatureHtml, type, setUser, setApplication, templateOverrideId, defaultTemplates])

    const handleSend = useCallback(async () => {
        if (!user || !contactEmail || !senderEmail) {
            if (isLoadHubEmailSurface(analyticsSurface)) {
                trackEmailValidationFailed({
                    load,
                    emailType: toDetailedEmailType(type),
                    composeId: analyticsComposeId,
                    errorCode: !user ? 'not_authenticated' : !contactEmail ? 'recipient_missing' : 'sender_missing',
                })
            }
            showSnackbar(SnackbarType.ERROR, !contactEmail ? 'No broker email found' : 'No sender email configured')
            return
        }
        if (!subject.trim() || !bodyHtml.trim()) {
            if (isLoadHubEmailSurface(analyticsSurface)) {
                trackEmailValidationFailed({
                    load,
                    emailType: toDetailedEmailType(type),
                    composeId: analyticsComposeId,
                    errorCode: 'content_missing',
                })
            }
            showSnackbar(SnackbarType.ERROR, 'Subject and body are required')
            return
        }

        const currentLoad = rateLoad(rateOverride)
        const currentRate = getPositiveEmailRate(currentLoad.rateAmountUsd)
        if (rateOverride !== undefined && type === 'offer' && currentRate == null) {
            if (isLoadHubEmailSurface(analyticsSurface)) {
                trackEmailValidationFailed({
                    load,
                    emailType: 'offer',
                    composeId: analyticsComposeId,
                    errorCode: 'invalid_offer_rate',
                })
            }
            showSnackbar(SnackbarType.ERROR, 'Enter a valid Price for Broker before placing the bid')
            return
        }

        setSending(true)
        if (isLoadHubEmailSurface(analyticsSurface)) trackEmailAttempted(load.sourceType, type)
        const loadHubEmailAnalytics = isLoadHubEmailSurface(analyticsSurface)
            ? trackEmailSendSubmitted({
                  load,
                  emailType: toDetailedEmailType(type),
                  recipientCount: contactEmails.length,
                  composeId: analyticsComposeId,
                  templateId: resolvedTemplate?._id,
              })
            : null
        try {
            // The calculator and compose used to synchronize through effects. A fast click could therefore
            // send the previous `$-` snapshot even while the calculator showed a rate. Generate untouched
            // content from the CURRENT rate at the submission boundary; edited content keeps the user's copy
            // and only has an unresolved `$-` marker filled.
            const currentSeed = buildSeed(rateOverride)
            const outbound = prepareEmailContentForSend({
                draftSubject: subject,
                draftBody: bodyHtml,
                currentSubject: currentSeed.subject,
                currentBody: currentSeed.body,
                subjectEdited: subjectEditedRef.current,
                bodyEdited: bodyEditedRef.current,
                rate: currentRate,
                rateLayout: type === 'offer' && rateOverride !== undefined ? buildSeed(RATE_SLOT_MARKER) : undefined,
            })
            const loadPayload = convertCanonicalLoadToV4Payload(currentLoad)
            const trimmedSignature = hideSignature ? '' : signatureHtml.trim()
            const htmlBody = trimmedSignature ? `${outbound.body}<br><br>${trimmedSignature}` : outbound.body
            const ccList = mergeCcRecipients(contactEmails, splitRecipientEmails(cc))

            const response = await beginEmailSend(
                () =>
                    sendOfferBookEmailCanonical({
                        load: loadPayload,
                        action: { type: 'email', callType: 'email' },
                        email: {
                            to: { email: contactEmail },
                            toRecipients: contactEmails,
                            subject: outbound.subject,
                            htmlBody,
                            // Backend bucket: 'booking' for book, else the generic 'info' (offer + info inquiries).
                            emailType: type === 'book' ? 'booking' : 'info',
                            emailReplyPreference: 'MANUAL',
                            cc: ccList,
                            senderEmail,
                        },
                        datSearchId: '',
                        userId: user.id,
                        // What this send asks the broker for, recorded server-side as a bid. Sent on
                        // the request that carries the email, so the record cannot outlive or miss
                        // the send it describes.
                        //
                        // Recorded only when the dispatcher's OWN number is driving this send, which is
                        // exactly `typeof rateOverride === 'number'`.
                        //
                        // `rateLoad` substitutes the rate only when it is non-null, so BOTH other
                        // states fall back to the load's own posted rate: `undefined` (no calculator —
                        // every Load Hub send) and `null` (the Box Truck price field CLEARED). Either
                        // would have recorded a "$700 bid" on a load posted at $700 that nobody typed,
                        // dragging every average over the table toward posted rates. Checking
                        // `!== undefined` alone missed the cleared case.
                        //
                        // `book` counts, not just `offer`: in the one surface that drives this, a
                        // booking carries the same typed figure and is the most committed ask there is.
                        // `info` asks for nothing and passes no override anyway.
                        ...(typeof rateOverride === 'number' && type !== 'info' && currentRate != null
                            ? {
                                  bidRate: currentRate,
                                  bodyEdited: bodyEditedRef.current,
                                  // Rides the SAME gate as the rate, not a gate of its own: a driver
                                  // without a bid is a row that claims an ask that was never made.
                                  ...(bidDriver ? { bidDriver: { id: bidDriver.id, ...(bidDriver.name ? { name: bidDriver.name } : {}) } } : {}),
                              }
                            : {}),
                        ...(loadHubEmailAnalytics ? { loadHubAnalytics: toServerEmailAnalytics(loadHubEmailAnalytics) } : {}),
                    }),
                onSendStarted
            )

            showSnackbar(SnackbarType.SUCCESS, `${type === 'book' ? 'Booking' : type === 'info' ? 'Info' : 'Offer'} email sent to ${contactEmail}`)
            if (isLoadHubEmailSurface(analyticsSurface)) {
                trackEmailCompleted(load.sourceType, type, response.alreadySent === true ? 'deduplicated' : 'sent', {
                    legacyRate: type === 'info' ? 0 : (currentRate ?? 0),
                })
            }
            void persistTemplate()
            const result = response.result
            onSent?.({
                emailThreadId: result?.emailThreadId,
                loadId: result?.load?._id != null ? String(result.load._id) : undefined,
                // Dedup: the server answered without sending anything. Callers that count sends — the box
                // truck pricing record — must not count this one twice.
                alreadySent: response.alreadySent === true,
                // The calculator's rate. For an UNEDITED body it is also what the broker read, because
                // the body is seeded from it. For an edited one it is not provable: `synchronizeEmailTemplateRate`
                // keeps the template's rate slots in step while the bid changes, but a number typed into
                // the body after the last bid change stays as typed. `bodyEdited` is reported alongside so
                // a caller that scores people can refuse to score what it cannot prove.
                sentRate: type === 'info' ? null : (currentRate ?? null),
                bodyEdited: bodyEditedRef.current,
            })
            onClose()
        } catch (err) {
            const authMessage = handleEmailAuthError(err, senderEmail)
            const msg = authMessage ?? (getUserErrorMessage(err) || 'Failed to send email')
            if (!authMessage) showSnackbar(SnackbarType.ERROR, msg)
            if (isLoadHubEmailSurface(analyticsSurface)) trackEmailCompleted(load.sourceType, type, 'failed', { error: err })
            if (loadHubEmailAnalytics) trackEmailRequestFailed(loadHubEmailAnalytics, 'transport', 'unknown', 'request_failed')
        } finally {
            setSending(false)
        }
    }, [
        user,
        contactEmail,
        contactEmails,
        senderEmail,
        load,
        rateLoad,
        rateOverride,
        bidDriver,
        buildSeed,
        subject,
        bodyHtml,
        signatureHtml,
        hideSignature,
        cc,
        type,
        onSendStarted,
        onSent,
        onClose,
        persistTemplate,
        analyticsSurface,
        analyticsComposeId,
        handleEmailAuthError,
        resolvedTemplate?._id,
    ])

    const isOffer = type === 'offer'

    const actions = (
        <div className={cn('flex items-center gap-2 pt-2', fullWidthSend ? 'w-full' : 'justify-end', !actionsContainer && 'mt-auto')}>
            {!hideCancel && (
                <button
                    onClick={onClose}
                    className="h-7 px-3 text-[12px] font-medium text-[var(--lh-fg-muted)] hover:text-[var(--lh-fg)] bg-transparent border-none cursor-pointer transition-colors"
                >
                    Cancel
                </button>
            )}
            <button
                onClick={handleSend}
                disabled={sending || !contactEmail || !senderEmail}
                className={cn(
                    'font-medium rounded-lg transition-colors border-none flex items-center cursor-pointer disabled:opacity-50',
                    fullWidthSend && 'w-full justify-center',
                    sendPrimary ? 'h-9 px-5 text-sm gap-2' : 'h-7 px-3 text-[12px] gap-1.5',
                    sendPrimary || !isOffer
                        ? 'bg-[var(--lh-accent)] hover:bg-[var(--lh-accent-hover)] text-[var(--lh-accent-foreground)]'
                        : 'bg-[var(--lh-surface-secondary)] hover:bg-[var(--lh-surface-tertiary)] text-[var(--lh-fg)] border border-[var(--lh-border)]'
                )}
            >
                {sending ? (
                    <>
                        <span
                            className={cn('border-[1.5px] border-current border-t-transparent rounded-full animate-spin', sendPrimary ? 'w-4 h-4' : 'w-3 h-3')}
                        />
                        Sending...
                    </>
                ) : (
                    <>
                        <svg className={sendPrimary ? 'w-4 h-4' : 'w-3 h-3'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                            />
                        </svg>
                        {sendLabel}
                    </>
                )}
            </button>
        </div>
    )

    return (
        <div className="flex flex-col w-full h-full min-w-0 px-1 py-1 overflow-y-auto">
            {/* Title */}
            {!hideHeader && (
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[12px] font-medium text-[var(--lh-fg)]">
                        {type === 'book' ? 'Booking Email' : type === 'info' ? 'Info Email' : 'Offer Email'}
                    </span>
                    <button
                        onClick={onClose}
                        className="w-6 h-6 flex items-center justify-center rounded-md border-none bg-transparent hover:bg-[var(--lh-surface-secondary)] transition-colors cursor-pointer"
                    >
                        <svg className="w-3 h-3 text-[var(--lh-fg-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {/* To + From — one row. To truncates so a long address never pushes From onto a second line. */}
            <div className="flex items-center gap-x-3 text-[12px] mb-2 min-w-0">
                <span className="min-w-0 truncate text-[var(--lh-fg-muted)]">
                    To: <span className="text-[var(--lh-accent)] font-semibold">{contactEmail || '—'}</span>
                    {contactEmails.length > 1 && ` (+${contactEmails.length - 1} more)`}
                </span>
                {/* From: with a sender picker (Box Truck) the label sits inline and the picker is the value,
                    so the operator switches the sending mailbox in place; otherwise it's the static address. */}
                {senderSelector ? (
                    <span className="flex shrink-0 items-center gap-1.5 text-[var(--lh-fg-muted)]">From: {senderSelector}</span>
                ) : (
                    <span className="shrink-0 text-[var(--lh-fg-muted)]">
                        From: <span className="text-[var(--lh-fg)] font-medium">{senderEmail || '—'}</span>
                    </span>
                )}
                {headerAction && <span className="ml-auto shrink-0">{headerAction}</span>}
            </div>

            {/* Subject */}
            <input
                type="text"
                value={subject}
                onChange={(e) => {
                    subjectEditedRef.current = true
                    setSubject(e.target.value)
                }}
                className="w-full h-8 px-2 mb-2 text-[12px] rounded-lg border border-[var(--lh-border)] bg-[var(--lh-surface-secondary)] text-[var(--lh-fg)] outline-none focus:border-[var(--lh-accent)] transition-colors"
                placeholder="Subject..."
            />

            {/* CC — hidden for Box Truck; the template's CC list still merges into the send either way. */}
            {!hideCc && (
                <input
                    type="text"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    className="w-full h-8 px-2 mb-2 text-[12px] rounded-lg border border-[var(--lh-border)] bg-[var(--lh-surface-secondary)] text-[var(--lh-fg)] outline-none focus:border-[var(--lh-accent)] transition-colors"
                    placeholder="CC (comma-separated)..."
                />
            )}

            {/* Body */}
            <div className="mb-2">
                {bodyIsReadOnly && readOnlyBody ? (
                    readOnlyBody.render(bodyHtml)
                ) : (
                    <TiptapEditor
                        content={bodyHtml}
                        onChange={handleBodyChange}
                        onFocus={() => {
                            bodyFocusedRef.current = true
                        }}
                        placeholder="Email body..."
                        minHeight={120}
                        preserveHtml={preserveHtml}
                        htmlPreviewRoleResolver={defaultTemplates[type].previewRoleResolver}
                        fitToWidth={fitBodyToWidth}
                    />
                )}
            </div>

            {/* Signature (with removable inline logo). Hidden for a template that carries its own footer. */}
            <div className={`mb-2 ${hideSignature ? 'hidden' : ''}`}>
                <span className="block text-[11px] font-medium text-[var(--lh-fg-muted)] mb-1">Signature</span>
                <SignatureFooterEditor
                    content={signatureHtml}
                    onChange={setSignatureHtml}
                    placeholder="Signature..."
                    minHeight={60}
                    onImageUpload={handleLogoUpload}
                />
            </div>

            {/* Actions — portaled under the calculator when `actionsContainer` is set (Box Truck Bidding
                side-by-side), else pinned inline at the bottom of the compose. */}
            {actionsContainer ? createPortal(actions, actionsContainer) : actions}
        </div>
    )
}
